import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ContentHeader from "../components/ContentHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORE = "msp_admin_key";

const PLATFORMS = ["LinkedIn", "Reddit", "Quora", "X (Twitter)", "Facebook", "Other"];

// Curated searches to find high-fit institutional posts worth replying to.
const BUYER_KEYWORDS = [
  { group: "LinkedIn search", items: [
    "mortgage servicing loss mitigation", "default management delinquency", "Reg X early intervention servicer",
    "foreclosure prevention lender", "loan modification forbearance servicing", "mortgage portfolio risk roll rate",
  ]},
  { group: "Reddit / Quora", items: [
    "mortgage servicer delinquency advice", "loss mitigation process credit union", "CFPB servicing compliance question",
    "reducing foreclosure rate portfolio", "borrower outreach strategy servicing",
  ]},
  { group: "Google (find discussions)", items: [
    '"mortgage servicing" "loss mitigation" site:reddit.com', '"default management" delinquency forum',
    '"foreclosure prevention" program servicer discussion', '"roll rate" mortgage servicing community',
  ]},
];

const VERDICT_LABEL = {
  strong_fit: "Strong buyer fit",
  possible_fit: "Possible fit",
  not_a_fit: "Not a fit — skip",
};

const ACTION_LABEL = {
  reply: "Reply with value",
  engage_lightly: "Engage lightly",
  skip: "Skip this one",
};

const scoreClass = (s) => (s >= 70 ? "sra-score-high" : s >= 40 ? "sra-score-mid" : "sra-score-low");

const LEAD_STATUSES = ["new", "replied", "won", "ignored"];
const LEAD_LABEL = { new: "New", replied: "Replied", won: "Won", ignored: "Ignored" };

const SocialReplyAssistant = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(KEY_STORE) || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(KEY_STORE));
  const [platform, setPlatform] = useState("LinkedIn");
  const [postText, setPostText] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [openId, setOpenId] = useState(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${adminKey}` }), [adminKey]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/social-replies?limit=25`, { headers: headers() });
      setHistory(data);
    } catch { /* non-blocking */ }
  }, [headers]);

  useEffect(() => { if (authed) loadHistory(); /* eslint-disable-next-line */ }, []);

  const login = async () => {
    if (!adminKey) return;
    try {
      await axios.get(`${API}/admin/articles`, { headers: { Authorization: `Bearer ${adminKey}` } });
      sessionStorage.setItem(KEY_STORE, adminKey);
      setAuthed(true);
      loadHistory();
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Invalid admin key");
      else toast.error("Login failed");
    }
  };

  const analyze = async () => {
    if (postText.trim().length < 15) {
      toast.error("Paste a longer post to analyze");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/social-reply`,
        { post_text: postText, platform, context },
        { headers: headers() }
      );
      setResult(data);
      loadHistory();
    } catch (e) {
      if (e?.response?.status === 401) { setAuthed(false); toast.error("Session expired — log in again"); }
      else toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const [regenTone, setRegenTone] = useState("");

  const regenerate = async (tone) => {
    if (postText.trim().length < 15) return;
    setRegenTone(tone);
    try {
      const { data } = await axios.post(
        `${API}/admin/social-reply/regenerate`,
        { post_text: postText, platform, context, tone },
        { headers: headers() }
      );
      setResult(data);
      toast.success(`Redrafted — ${tone} tone`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate");
    } finally {
      setRegenTone("");
    }
  };

  const copy = (label, text) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error("Copy failed"));
  };

  const deleteHistory = async (id) => {
    try {
      await axios.delete(`${API}/admin/social-replies/${id}`, { headers: headers() });
      setHistory((h) => h.filter((x) => x.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const setLeadStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/social-replies/${id}`, { status }, { headers: headers() });
      setHistory((h) => h.map((x) => (x.id === id ? { ...x, lead_status: status } : x)));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  if (!authed) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <ContentHeader />
        <div className="hero-bg" />
        <div className="admin-gate" data-testid="sra-gate">
          <h1>Social Reply Assistant</h1>
          <p style={{ color: "var(--g1)", marginBottom: "1.4rem", fontSize: "0.9rem" }}>
            Enter your admin key to qualify leads and draft replies.
          </p>
          <input
            className="admin-input"
            type="password"
            placeholder="Admin key"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            data-testid="sra-key-input"
          />
          <button className="admin-btn" onClick={login} disabled={!adminKey} data-testid="sra-login-btn">
            Access Assistant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh" }} data-testid="sra-dashboard">
      <ContentHeader />
      <div className="hero-bg" />
      <div className="admin-wrap">
        <div className="admin-header">
          <div>
            <h1>Social Reply Assistant</h1>
            <p style={{ color: "var(--g1)", fontSize: "0.85rem", marginTop: "0.3rem", maxWidth: "640px" }}>
              Paste a public post from a prospect. The AI scores whether they're a real institutional
              buyer, then drafts a compliant, value-first reply you review and post yourself.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <button className="admin-btn-ghost" onClick={() => navigate("/admin/articles")} data-testid="sra-to-articles">Content Agent</button>
          </div>
        </div>

        <div className="sra-grid">
          <div className="sra-panel" data-testid="sra-input-panel">
            <label className="sra-label">Platform</label>
            <div className="sra-platforms">
              {PLATFORMS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`sra-chip ${platform === p ? "sra-chip-on" : ""}`}
                  onClick={() => setPlatform(p)}
                  data-testid={`sra-platform-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>

            <label className="sra-label" htmlFor="sra-post">The public post (paste it here)</label>
            <textarea
              id="sra-post"
              className="sra-textarea"
              rows={9}
              placeholder="Paste the exact text of the post or comment you're thinking about replying to…"
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
              data-testid="sra-post-input"
            />

            <label className="sra-label" htmlFor="sra-ctx">Optional context (who they are, link, etc.)</label>
            <input
              id="sra-ctx"
              className="admin-input"
              style={{ margin: 0 }}
              placeholder="e.g. VP of Servicing at a regional bank"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              data-testid="sra-context-input"
            />

            <button className="admin-btn" style={{ marginTop: "1rem" }} onClick={analyze} disabled={loading} data-testid="sra-analyze-btn">
              {loading ? "Analyzing…" : "Qualify & Draft Reply"}
            </button>
          </div>

          <div className="sra-panel" data-testid="sra-result-panel">
            {!result && !loading && (
              <div className="sra-empty" data-testid="sra-empty">
                Your lead score and suggested reply will appear here.
              </div>
            )}
            {loading && <div className="sra-empty">Reading the post and drafting a reply…</div>}

            {result && (
              <div data-testid="sra-result">
                <div className="sra-scorecard">
                  <div className={`sra-score ${scoreClass(result.buyer_fit_score)}`} data-testid="sra-score">
                    {result.buyer_fit_score}
                    <span>/100</span>
                  </div>
                  <div className="sra-verdict">
                    <div className={`sra-badge sra-badge-${result.verdict}`} data-testid="sra-verdict">
                      {VERDICT_LABEL[result.verdict] || result.verdict}
                    </div>
                    <div className="sra-action" data-testid="sra-action">
                      Recommended: <strong>{ACTION_LABEL[result.recommended_action] || result.recommended_action}</strong>
                    </div>
                  </div>
                </div>

                {result.audience_read && (
                  <div className="sra-block">
                    <span className="sra-block-label">Who they are</span>
                    <p data-testid="sra-audience">{result.audience_read}</p>
                  </div>
                )}
                {result.reasoning && (
                  <div className="sra-block">
                    <span className="sra-block-label">Why</span>
                    <p data-testid="sra-reasoning">{result.reasoning}</p>
                  </div>
                )}

                {result.draft_reply ? (
                  <>
                    <div className="sra-block">
                      <div className="sra-reply-head">
                        <span className="sra-block-label">Suggested reply</span>
                        <button className="admin-btn-ghost" onClick={() => copy("Reply", result.draft_reply)} data-testid="sra-copy-reply">Copy</button>
                      </div>
                      <div className="sra-reply" data-testid="sra-draft-reply">{result.draft_reply}</div>
                      <div className="sra-tone-row" data-testid="sra-tone-row">
                        <span className="sra-tone-label">Regenerate:</span>
                        <button className="sra-tone-btn" disabled={!!regenTone} onClick={() => regenerate("warmer")} data-testid="sra-tone-warmer">{regenTone === "warmer" ? "…" : "Warmer"}</button>
                        <button className="sra-tone-btn" disabled={!!regenTone} onClick={() => regenerate("shorter")} data-testid="sra-tone-shorter">{regenTone === "shorter" ? "…" : "Shorter"}</button>
                        <button className="sra-tone-btn" disabled={!!regenTone} onClick={() => regenerate("direct")} data-testid="sra-tone-direct">{regenTone === "direct" ? "…" : "More direct"}</button>
                      </div>
                    </div>
                    {result.short_reply && (
                      <div className="sra-block">
                        <div className="sra-reply-head">
                          <span className="sra-block-label">Short version</span>
                          <button className="admin-btn-ghost" onClick={() => copy("Short reply", result.short_reply)} data-testid="sra-copy-short">Copy</button>
                        </div>
                        <div className="sra-reply sra-reply-short" data-testid="sra-short-reply">{result.short_reply}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="sra-skip" data-testid="sra-skip-note">
                    Not a buyer — no reply drafted. Engaging here would likely be spammy or off-target.
                  </div>
                )}

                {result.compliance_notes && (
                  <div className="sra-compliance" data-testid="sra-compliance">
                    <span className="sra-block-label">Compliance &amp; tone notes</span>
                    <p>{result.compliance_notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="sra-panel sra-keywords" data-testid="sra-keywords">
          <span className="sra-block-label">Buyer finder — searches to find high-fit prospects</span>
          <p className="sra-keywords-hint">Paste these into the platform's search bar to surface posts from real servicers, banks &amp; credit unions. Click to copy.</p>
          <div className="sra-kw-groups">
            {BUYER_KEYWORDS.map((g) => (
              <div className="sra-kw-group" key={g.group}>
                <div className="sra-kw-title">{g.group}</div>
                <div className="sra-kw-chips">
                  {g.items.map((kw) => (
                    <button key={kw} className="sra-kw-chip" onClick={() => copy("Search", kw)} data-testid={`sra-kw-${kw.slice(0, 12)}`}>{kw}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="sra-panel sra-history" data-testid="sra-history">
          <span className="sra-block-label">Lead tracker — {history.length} analyzed {history.length === 1 ? "post" : "posts"}</span>
          {history.length === 0 && <p className="sra-keywords-hint">Posts you analyze will be saved here with their buyer score and drafted reply.</p>}
          {history.map((h) => (
            <div className="sra-hist-row" key={h.id} data-testid={`sra-hist-${h.id}`}>
              <div className="sra-hist-head" onClick={() => setOpenId(openId === h.id ? null : h.id)}>
                <span className={`sra-hist-score ${scoreClass(h.buyer_fit_score)}`}>{h.buyer_fit_score}</span>
                <div className="sra-hist-main">
                  <div className="sra-hist-meta">
                    <span className={`sra-badge sra-badge-${h.verdict}`}>{VERDICT_LABEL[h.verdict] || h.verdict}</span>
                    <span className={`sra-lead-badge sra-lead-${h.lead_status || "new"}`}>{LEAD_LABEL[h.lead_status || "new"]}</span>
                    <span className="sra-hist-platform">{h.platform}</span>
                  </div>
                  <div className="sra-hist-snippet">{(h.post_text || "").slice(0, 120)}{(h.post_text || "").length > 120 ? "…" : ""}</div>
                </div>
                <button className="admin-btn-ghost admin-btn-danger" onClick={(e) => { e.stopPropagation(); deleteHistory(h.id); }} data-testid={`sra-hist-del-${h.id}`}>Delete</button>
              </div>
              {openId === h.id && (
                <div className="sra-hist-body" data-testid={`sra-hist-body-${h.id}`}>
                  <div className="sra-lead-actions" data-testid={`sra-lead-actions-${h.id}`}>
                    <span className="sra-tone-label">Pipeline:</span>
                    {LEAD_STATUSES.map((s) => (
                      <button
                        key={s}
                        className={`sra-lead-btn ${(h.lead_status || "new") === s ? "sra-lead-btn-on" : ""}`}
                        onClick={() => setLeadStatus(h.id, s)}
                        data-testid={`sra-lead-set-${s}-${h.id}`}
                      >{LEAD_LABEL[s]}</button>
                    ))}
                  </div>
                  <div className="sra-block"><span className="sra-block-label">Full post</span><p>{h.post_text}</p></div>
                  {h.reasoning && <div className="sra-block"><span className="sra-block-label">Why</span><p>{h.reasoning}</p></div>}
                  {h.draft_reply ? (
                    <div className="sra-block">
                      <div className="sra-reply-head">
                        <span className="sra-block-label">Drafted reply</span>
                        <button className="admin-btn-ghost" onClick={() => copy("Reply", h.draft_reply)}>Copy</button>
                      </div>
                      <div className="sra-reply">{h.draft_reply}</div>
                    </div>
                  ) : <div className="sra-skip">No reply drafted (not a buyer).</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialReplyAssistant;
